import {useEffect, useState} from "react";
import {Descendant} from "slate";
import jsPDF from "jspdf";
import {
    isMobile,
    isTablet,
    isDesktop,
    browserName,
    osName,
    deviceType,
    getUA,
} from "react-device-detect";
import {Timestamp} from "firebase/firestore";

export const useCheckMobileScreen = () => {
    const [width, setWidth] = useState(window.innerWidth);
    const handleWindowSizeChange = () => {
        setWidth(window.innerWidth);
    };

    useEffect(() => {
        window.addEventListener("resize", handleWindowSizeChange);
        return () => {
            window.removeEventListener("resize", handleWindowSizeChange);
        };
    }, []);

    return (width <= 540);
};


function getWindowDimensions() {
    const {innerWidth: width, innerHeight: height} = window;
    return {
        width,
        height,
    };
}

export default function useWindowDimensions() {
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

    useEffect(() => {
        function handleResize() {
            setWindowDimensions(getWindowDimensions());
        }

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return windowDimensions;
}

export const serialiseSlate = (nodes: Descendant[]) => {
    return nodes.map((n:any) => {
        if (n.type === "paragraph") {
            const text=[];
            for (let i = 0; i < n.children.length; i++) {
                const item = n.children[i];
                let subtext = item.text || <br/>;
                if (item.underline) {
                    subtext = <u>{subtext}</u>;
                }
                if (item.bold) {
                    subtext = <strong>{subtext}</strong>;
                }
                if (item.italic) {
                    subtext = <i>{subtext}</i>;
                }
                text.push(subtext);
            }
            return <p>{text}</p>;
        }
    });
};


export function imageToPdf(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        // Check if the file is an image
        if (!file.type.startsWith("image/")) {
            reject(new Error("The provided file is not an image"));
            return;
        }

        // Create a new Image object
        const img = new Image();
        img.src = URL.createObjectURL(file);

        img.onload = () => {
            // Create a canvas and draw the image on it
            const canvas = document.createElement("canvas");
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const ctx = canvas.getContext("2d")!;
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);

            // Convert canvas to base64
            const imageData = canvas.toDataURL("image/jpeg");

            // Create a new jsPDF instance
            // eslint-disable-next-line new-cap
            const pdf = new jsPDF({
                orientation: img.width > img.height ? "landscape" : "portrait",
                unit: "px",
                format: [img.width, img.height],
            });

            // Add image to PDF
            pdf.addImage(imageData, "JPEG", 0, 0, img.width, img.height);

            // Output PDF as base64
            const base64String = pdf.output("datauristring").split(",")[1];

            // Replace the MIME type to 'application/pdf'
            resolve(base64String.replace("data:application/octet-stream", "data:application/pdf"));
        };

        img.onerror = (error) => {
            reject(error);
        };
    });
}

export const formatPrice = (price: number, noSign?: boolean) => {
    const pricefromPence = (price/100).toString();
    const pounds = pricefromPence.split(".")?.[0];
    const pence = pricefromPence.split(".")?.[1];

    const penceFormatted = pence ?
        pence.length === 2 ? pence : pence.length > 2 ? pence.substring(0, 2) : (pence + "0") : "00";

    const finalCost = `${pounds}.${penceFormatted}`;
    return noSign ? finalCost : `£${finalCost}`;
};


export function stringToRGBColor(str: string): string {
    // Hash the string to generate a numeric value
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Use the hash to generate RGB values with ranges to avoid unpleasant colors
    const r = (hash & 0xFF) % 156 + 100; // Range: 50-205
    const g = ((hash >> 8) & 0xFF) % 156 + 100; // Range: 50-205
    const b = ((hash >> 16) & 0xFF) % 156 + 100; // Range: 50-205

    return `rgb(${r}, ${g}, ${b})`;
}


export const renderSlate = (text: Descendant[]) => {
    return (text.map((element: any) => {
        if (element.type === "paragraph") {
            return <p>{element.children.map((child: {text: string}) => <span>{child.text}</span>)}</p>;
        }
        if (element.type === "link") {
            return <a href={element.href} target={"_blank"}>{element.children.map((child: {text: string}) => <span>{child.text}</span>)}</a>;
        }
    }));
};

export function getDeviceInfo() {
    return {
        deviceType: deviceType || (isMobile ? "mobile" : isTablet ? "tablet" : isDesktop ? "desktop" : "unknown"),
        os: osName || "unknown",
        browser: browserName || "unknown",
        ua: getUA,
    };
}


export function objectsEqualNew(a: any, b: any): boolean { // Works with arrays or objects.
    if (a === b) {
        return true;
    }

    if (typeof a !== typeof b) {
        return false;
    }

    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) {
            return false;
        }
        for (let i = 0; i < a.length; i++) {
            if (!objectsEqualNew(a[i], b[i])) {
                return false;
            }
        }
        return true;
    }

    if (typeof a === "object" && typeof b === "object" && a !== null && b !== null) {
        const keysA = Object.keys(a);
        const keysB = Object.keys(b);

        if (keysA.length !== keysB.length) {
            return false;
        }

        for (const key of keysA) {
            if (!keysB.includes(key)) {
                return false;
            }
            if (!objectsEqualNew(a[key], b[key])) {
                return false;
            }
        }
        return true;
    }

    return false;
}

export const getProviderId = async () => {/*
    let user = await getDoc(doc(db, 'users', auth.currentUser.uid))
    return user.data().bi*/
};

export const getRandomNumber = (min = 0, max:number) => {
    return Math.random() * (max - min) + min;
};

export const getUniqueId = (object:object): number => {
    const id = Math.round(getRandomNumber(0, 1000000));

    if (!object) {
        return id;
    }

    if (Array.isArray(object)) {
        return object.some((el) => el.id === id) ? getUniqueId(object) : id;
    }

    return (Object.keys(object) as any[]).some((el) => el === id) ? getUniqueId(object) : id;
};

export const capitalise = (s="") => {
    const nS = s.toString().replace("_", " ");
    return nS.charAt(0).toUpperCase() + nS.slice(1);
};

export const capitaliseWords = (s="") => {
    return s.replace(/(^|\s)\S/g, (l) => l.toUpperCase());
};

export const snakeCase = (s="") => {
    const nS = s.toString().replace(" ", "_");
    return nS.toLowerCase();
};

export const validateEmail = (email: string) => {
    console.log(email);
    return email.match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

export const dateToString = (date:Date) => {
    const y = date.getFullYear();
    const m = ("0"+(date.getMonth() + 1)).slice(-2);
    const d = ("0"+(date.getDate())).slice(-2);
    return [y, m, d].join("-");
};

export const getPlacementDateArray = (start:string, end:string) => {
    const dates = [start];
    let currentDate = start;

    while (currentDate !== end) {
        const dateObj = new Date(currentDate);
        dateObj.setDate(dateObj.getDate()+1);
        const dateObjString = dateToString(dateObj);
        dates.push(dateObjString);
        currentDate = dateObjString;
    }

    return dates;
};

export const reformatDate = (date: Date|string) => {
    if (!date) {
        return;
    }
    return date.toString().split("-").reverse().join(".");
};

export const convertDate = (date?: string|Date, output: "dbstring"|"date"|"visual" = "date") => {
    // dbstring = yyyy-MM-dd
    // visual = dd MMM yyyy
    // date = Date
    if (!date) return;

    let year:number;
    let month:number;
    let day:number;

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    if (typeof date === "string") {
        const dateType = date.includes(" ") ? "visual" : "dbstring";
        const dateArray = date.split(dateType === "visual" ? " " : "-");

        year = parseInt(dateArray[dateType === "visual" ? 2 : 0]);
        month = dateType === "visual" ? months.indexOf(dateArray[1]) : parseInt(dateArray[1]) - 1;
        day = parseInt(dateArray[dateType === "visual" ? 0 : 2]);
    } else {
        year = date.getFullYear();
        month = date.getMonth();
        day = date.getDate();
    }

    switch (output) {
    case "date":
        return new Date(year, month, day);
    case "dbstring":
        return [year, month+1 < 10 ? `0${month+1}` : month+1, day < 10 ? `0${day}` : day].join("-");
    case "visual":
        return [day, months[month], year].join(" ");
    }
};

export const getDateDiff = (start: Date, end:Date): number => {
    return (Math.round((new Date(dateToString(end)).getTime() - new Date(dateToString(start)).getTime()) / (1000 * 60 * 60 * 24)));
};

export const reformatDateTime = (timestamp: string|Timestamp) => {
    if (typeof timestamp === "string") {
        return `${timestamp.split("T")[1].substring(0, 8)} ${(timestamp.split("T")[0]).split("-").reverse().join(".")}`;
    }

    try {
        const created = new Date(timestamp.seconds * 1000).toISOString();
        return `${created.split("T")[1].substring(0, 8)} ${(created.split("T")[0]).split("-").reverse().join(".")}`;
    } catch (e) {
        return null;
    }
};

export function stringToDate(date: string) {
    const formatItems= date.split("/").map((i) => parseInt(i));
    console.log("date array", formatItems);
    return new Date(formatItems[2], formatItems[1]-1, formatItems[0]);
}

// export const editNestedObject = (loc:(string|number)[], data:any[]|{[key: string|number]: unknown}, value:any, objectId=false) => {
//     if (!data || !(Array.isArray(data) || typeof data === "object")) {
//         console.error("Error editing nested JSON. Invalid type");
//         return;
//     }

//     type IndexType = typeof data extends any[] ? number : number|string

//     const prevData: typeof data = Array.isArray(data) ? [...data] : {...data};
//     const locationIndex:IndexType = objectId && Array.isArray(data) ? (prevData as any[]).findIndex((x) => x.id === loc[0]) : loc[0];

//     if (loc.length > 1) {
//         prevData[locationIndex as any] = editNestedObject(loc.slice(1), data[locationIndex as any] || {}, value, objectId);
//     } else {
//         if (value === undefined) {
//             Array.isArray(prevData) ? prevData.splice(locationIndex as number, 1) : delete prevData[locationIndex];
//         } else {
//             prevData[locationIndex as any] = value;
//         }
//     }

//     return prevData;
// };

export const camelCaseToNormal = (string="") => {
    return string.replace(/([A-Z])/g, " $1").toLowerCase().trim().replace(/^./, function(str) {
        return str.toUpperCase();
    });
};

export const camelCase = (string="") => {
    return string.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());
};

export const isJson = (item:any) => {
    item = typeof item !== "string" ?
        JSON.stringify(item) :
        item;

    try {
        item = JSON.parse(item);
    } catch (e) {
        return false;
    }

    return (typeof item === "object" && item !== null);
};

export const sortByReverseStringLength = (array: string[]) => {
    return array.sort(function(a, b) {
        return b.length - a.length;
    });
};

export const pathToArr = (path: any[]|any) => Array.isArray(path) ? path : [path];

export const arraysEqual = (a:any[], b:any[]) => {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (a.length !== b.length) return false;

    return a.every((val, idx) => val === b[idx]);
};


export const objectsEqual = (x:object, y:object):boolean => {
    const ok = Object.keys; const tx = typeof x; const ty = typeof y;
    return x && y && tx === "object" && tx === ty ? (
        ok(x).length === ok(y).length &&
        ok(x).every((key) => objectsEqual(x[key as keyof typeof x], y[key as keyof typeof x]))
    ) : (x === y);
};

export const arrayUniqueValues = (array:unknown[]) => {
    return array.filter((value, index, self) => self.indexOf(value) === index);
};

export const randomPassword = () => {
    let text = "";
    const c = "ABCDEFGHIJKLMNNOPQRSTUVWXYZ" +
            "abcdefghijklmnopqrstuvwxyz0123456789";

    for (let i = 0; i < 12; i++) {
        text += c.charAt(Math.floor(Math.random() * c.length));
    }
    return text;
};

export const arrayEquals = (a:unknown, b:unknown) => {
    return Array.isArray(a) &&
        Array.isArray(b) &&
        a.length === b.length &&
        a.every((val, index) => val === b[index]);
};

export const average = (arr: number[]) => arr.reduce( ( p, c ) => p + c, 0 ) / arr.length;

export const readingTime = (text: string) => {
    if (!text) return;
    const wpm = 225;
    const words = text.trim().split(/\s+/).length;
    const time = Math.ceil(words / wpm);
    return `${time} minute${(time > 1) ? "s" : ""}`;
};
